export const IMPORTANCE_OPTIONS = [
  { value: "unknown", label: "Not sure yet" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export const URGENCY_OPTIONS = [
  { value: "unknown", label: "Not sure yet" },
  { value: "immediate", label: "Immediate" },
  { value: "soon", label: "Soon" },
  { value: "flexible", label: "Flexible" },
];

export function readinessLabel(candidate) {
  if (!candidate) return "Unknown";
  if (candidate.validity && candidate.validity !== "valid") return "Unavailable";
  if (candidate.readiness?.alreadyDeliveredOrInReview) return "In review";
  if (candidate.readiness?.ready) return "Ready";
  if (candidate.readiness?.unverifiable) return "Unknown";
  return "Blocked";
}

export function provenanceLabel(provenance) {
  switch (provenance) {
    case "confirmed":
      return "Confirmed";
    case "provided_by_you":
      return "Provided by you";
    case "inferred":
      return "Inferred";
    default:
      return "Unknown";
  }
}

export function bucketLabel(bucket) {
  switch (bucket) {
    case "include":
      return "Include";
    case "do_first":
      return "Do first";
    case "wait":
      return "Wait";
    case "needs_decision":
      return "Needs your decision";
    default:
      return "Unknown";
  }
}

export function projectSprintView(design) {
  if (!design) return null;
  const assessment = design.assessment || {};
  const recommendation = design.recommendation || null;
  const includeIds = recommendation?.includeIds || assessment.includeIds || [];
  const waitIds = recommendation?.waitIds || assessment.waitIds || [];
  const needsIds = recommendation?.needsDecisionIds || assessment.needsDecisionIds || [];
  const doFirst = recommendation?.doFirstIds || assessment.doFirstIds || [];
  const byId = new Map((design.candidates || []).map((c) => [c.featurePlanId, c]));
  const reasonFor = (id) => {
    const item = recommendation?.items?.find((row) => row.featurePlanId === id);
    const ranked = assessment.ranked?.find((row) => row.featurePlanId === id);
    return item?.rationale || ranked?.reasons?.join(" ") || "";
  };
  return {
    id: design.id,
    sprintId: design.sprintId,
    version: design.version,
    revision: design.revision,
    contentFingerprint: design.contentFingerprint,
    status: design.status,
    title: design.title || "Next sprint",
    sprintGoal: design.sprintGoal || "",
    capacity: design.capacity,
    success: design.success || { statement: "", checks: [] },
    immutable: design.status === "committed" || design.status === "superseded",
    capacityMessage: assessment.capacity?.message || "Capacity cannot be confirmed",
    capacityFits: assessment.capacity?.fits,
    recommendationFallback: Boolean(recommendation?.fallback),
    fallbackReason: recommendation?.fallbackReason || "",
    include: includeIds.map((id) => ({ candidate: byId.get(id), reason: reasonFor(id), doFirst: doFirst.includes(id) })),
    wait: waitIds.map((id) => ({ candidate: byId.get(id), reason: reasonFor(id) })),
    needsDecision: needsIds.map((id) => ({ candidate: byId.get(id), reason: reasonFor(id) })),
    candidates: design.candidates || [],
    overlaps: assessment.overlaps || [],
    dependencyIssues: assessment.dependencyIssues || [],
    receipt: design.commitmentReceipt || null,
    preview: design.commitPreview || [],
  };
}

export function containsProhibitedInternalName(text) {
  return /featureplan|intentcontract|workbriefid|orchestration fingerprint|deterministic rule/i.test(
    String(text || "")
  );
}

export function factorProvenance(kind) {
  return provenanceLabel(kind);
}

export function candidateOverlapSummary(candidate, overlaps) {
  const rows = (overlaps || []).filter(
    (item) => item.leftFeaturePlanId === candidate.featurePlanId || item.rightFeaturePlanId === candidate.featurePlanId
  );
  if (rows.some((item) => item.kind === "exact_file")) {
    return { text: "Same files as another Task", provenance: "confirmed" };
  }
  if (rows.some((item) => item.kind === "module")) {
    return { text: "Same area as another Task", provenance: "inferred" };
  }
  if (rows.some((item) => item.kind === "unknown") || !candidate.exactFiles?.length) {
    return { text: "Overlap unknown — not independent", provenance: "unknown" };
  }
  return { text: "No shared files found", provenance: "confirmed" };
}

export function acceptanceCoverageLabel(coverage) {
  switch (coverage) {
    case "accepted":
      return "Already accepted";
    case "reviewing":
      return "In review";
    case "partial":
      return "Some checks exist";
    case "none":
      return "No checks yet";
    case "rejected":
      return "Returned for rework";
    default:
      return "Unknown";
  }
}
